/**
 * AI Service for ClutterLogix
 * Handles API calls to AI endpoints for image analysis,
 * description generation, and tag extraction.
 */
import axios from 'axios';
import { getBackendBaseUrl } from '../utils/configUtils';
import tagTasksService from './tagTasksService';
import api from './api';

// API settings
const API_TIMEOUT = 60000; // 60 seconds - AI processing can take time
const MAX_RETRIES = 1; // Number of retries for failed requests
const RETRY_DELAY = 1000; // Delay between retries in ms

const getAuthHeader = () => {
  const token = localStorage.getItem('cubxly_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Create a configured axios instance with timeout and headers
 * @returns {Object} - Configured axios instance
 */
const createAxiosInstance = () => {
  return axios.create({
    timeout: API_TIMEOUT,
    headers: getAuthHeader()
  });
};

/**
 * Sleep function for retry delay
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Handle API errors with detailed messages
 * @param {Error} error - The error object
 * @returns {Error} - Enhanced error with better message
 */
const handleApiError = (error, operation) => {
  let errorMessage = `Error ${operation}`;  
  
  if (error.response) {
    // Server responded with error
    const status = error.response.status;
    const serverMsg = error.response.data?.error || 'Unknown server error';
    const details = error.response.data?.details;
    
    if (status === 404) {
      errorMessage = `Resource not found: ${serverMsg}`;
      if (details) {
        errorMessage += ` (${details})`;
      }
    } else if (status === 500) {
      errorMessage = `AI processing error: ${serverMsg}`;
    } else {
      errorMessage = `Server error (${status}): ${serverMsg}`;
    }
  } else if (error.request) {
    // Request made but no response
    if (error.code === 'ECONNABORTED') {
      errorMessage = 'Request timed out. AI processing may take longer than expected.';
    } else {
      errorMessage = 'No response from server. Please check your connection.';
    }
  } else {
    // Error setting up request
    errorMessage = `Request setup error: ${error.message}`;
  }
  
  console.error(errorMessage, error);
  const enhancedError = new Error(errorMessage);
  enhancedError.originalError = error;
  return enhancedError;
};

const aiService = {
  /**
   * Analyze an item's image to generate a description and extract tags.
   * @param {number} itemId - ID of the item to analyze
   * @returns {Promise<Object>} - Analysis results with description, tags, and category
   */
  analyzeItem: async (itemId) => {
    const client = createAxiosInstance();
    let retries = 0;
    
    // Get the backend base URL
    const baseUrl = getBackendBaseUrl();
    
    while (true) {
      try {
        console.log(`Analyzing item ${itemId}${retries > 0 ? ` (retry ${retries})` : ''}`);
        // Prevent duplicate 'api' in URL path
        const apiPath = baseUrl.endsWith('/api') ? '/v2/ai/analyze-item/' : '/api/v2/ai/analyze-item/';
        const response = await client.post(`${baseUrl}${apiPath}${itemId}`, {});
        return response.data;
      } catch (error) {
        if (retries < MAX_RETRIES) {
          retries++;
          console.log(`Retrying analyze (${retries}/${MAX_RETRIES})...`);
          await sleep(RETRY_DELAY);
        } else {
          throw handleApiError(error, 'analyzing item');
        }
      }
    }
  },

  /**
   * Generate a description for an item based on its image.
   * @param {number} itemId - ID of the item
   * @returns {Promise<Object>} - Generated description
   */
  generateDescription: async (itemId) => {
    const client = createAxiosInstance();
    let retries = 0;
    
    // Get the backend base URL
    const baseUrl = getBackendBaseUrl();
    
    while (true) {
      try {
        console.log(`Generating description for item ${itemId}${retries > 0 ? ` (retry ${retries})` : ''}`);
        // Use V2 AI endpoints for PostgreSQL compatibility
        const apiPath = baseUrl.endsWith('/api') ? '/v2/ai/generate-description/' : '/api/v2/ai/generate-description/';
        const response = await client.post(`${baseUrl}${apiPath}${itemId}`, {});
        return response.data;
      } catch (error) {
        if (retries < MAX_RETRIES) {
          retries++;
          console.log(`Retrying description generation (${retries}/${MAX_RETRIES})...`);
          await sleep(RETRY_DELAY);
        } else {
          throw handleApiError(error, 'generating description');
        }
      }
    }
  },

  /**
   * Extract both description and tags from an item's image in a single API call.
   * More cost-effective and faster than separate calls.
   * @param {number} itemId - ID of the item
   * @returns {Promise<Object>} - Generated description and tags
   */
  extractDescriptionAndTags: async (itemId) => {
    const client = createAxiosInstance();
    const baseUrl = getBackendBaseUrl();
    
    console.log(`Queuing AI processing for item ${itemId}`);
    const apiPath = baseUrl.endsWith('/api') ? '/v2/ai/extract-description-and-tags/' : '/api/v2/ai/extract-description-and-tags/';
    const response = await client.post(`${baseUrl}${apiPath}${itemId}`, {});
    return response.data;
  },

  /**
   * Poll item status until AI processing completes.
   * @param {number} itemId - ID of the item
   * @param {function} onStatusUpdate - Callback for status updates (optional)
   * @param {number} maxWaitMs - Maximum wait time in ms (default 120000 = 2 minutes)
   * @param {number} pollIntervalMs - Poll interval in ms (default 2000 = 2 seconds)
   * @returns {Promise<Object>} - Updated item data when processing completes
   */
  pollItemStatus: async (itemId, onStatusUpdate = null, maxWaitMs = 120000, pollIntervalMs = 2000) => {
    const client = createAxiosInstance();
    const baseUrl = getBackendBaseUrl();
    const startTime = Date.now();
    
    console.log(`Starting to poll AI processing status for item ${itemId}`);
    
    while (Date.now() - startTime < maxWaitMs) {
      try {
        const apiPath = baseUrl.endsWith('/api') ? `/v2/items/${itemId}` : `/api/v2/items/${itemId}`;
        const response = await client.get(`${baseUrl}${apiPath}`);
        const item = response.data;
        
        const status = item.ai_processing_status || 'pending';
        console.log(`Item ${itemId} AI processing status: ${status}`);
        
        if (onStatusUpdate) {
          onStatusUpdate(status, item);
        }
        
        if (status === 'completed') {
          console.log(`AI processing completed for item ${itemId}`);
          return {
            status: 'completed',
            description: item.description,
            tags: item.custom_tags ? item.custom_tags.split(',').map(t => t.trim()).filter(t => t) : [],
            item: item
          };
        } else if (status === 'failed') {
          throw new Error('AI processing failed');
        } else if (status === 'content_rejected') {
          return {
            status: 'content_rejected',
            error: 'Image content was rejected by content screening'
          };
        }
        
        // Still processing, wait and poll again
        await sleep(pollIntervalMs);
      } catch (error) {
        console.error(`Error polling item status: ${error.message}`);
        throw error;
      }
    }
    
    throw new Error('AI processing timed out');
  },

  /**
   * Queue AI processing and wait for completion (convenience method).
   * @param {number} itemId - ID of the item
   * @param {function} onStatusUpdate - Callback for status updates (optional)
   * @returns {Promise<Object>} - Generated description and tags
   */
  extractDescriptionAndTagsWithPolling: async (itemId, onStatusUpdate = null) => {
    // Queue the processing
    const queueResponse = await aiService.extractDescriptionAndTags(itemId);
    
    // If already completed (shouldn't happen with new backend), return directly
    if (queueResponse.description) {
      return queueResponse;
    }
    
    // Poll for completion
    return await aiService.pollItemStatus(itemId, onStatusUpdate);
  },

  /**
   * Extract tags from an item's image.
   * @deprecated Use queueTagExtraction + pollTagExtractionStatus instead, or better yet use extractDescriptionAndTags
   * @param {number} itemId - ID of the item
   * @returns {Promise<Object>} - Extracted tags
   */
  extractTags: async (itemId) => {
    const client = createAxiosInstance();
    let retries = 0;
    
    // Get the backend base URL
    const baseUrl = getBackendBaseUrl();
    
    while (true) {
      try {
        console.log(`Extracting tags for item ${itemId}${retries > 0 ? ` (retry ${retries})` : ''}`);
        // Prevent duplicate 'api' in URL path - using v2 API with POST method
        const apiPath = baseUrl.endsWith('/api') ? '/v2/tags/extract/' : '/api/v2/tags/extract/';
        const response = await client.post(`${baseUrl}${apiPath}${itemId}`);
        return response.data;
      } catch (error) {
        if (retries < MAX_RETRIES) {
          retries++;
          console.log(`Retrying tag extraction (${retries}/${MAX_RETRIES})...`);
          await sleep(RETRY_DELAY);
        } else {
          throw handleApiError(error, 'extracting tags');
        }
      }
    }
  },

  /**
   * Queue asynchronous tag extraction using the background worker
   * @param {number} itemId - ID of the item
   * @returns {Promise<Object>} - Task information with task_id
   */
  queueTagExtraction: async (itemId) => {
    try {
      console.log(`Queueing tag extraction for item ${itemId} using background service`);
      return await tagTasksService.queueTagExtraction(itemId);
    } catch (error) {
      throw handleApiError(error, 'queueing tag extraction');
    }
  },
  
  /**
   * Check the status of a tag extraction task
   * @param {string} taskId - ID of the task
   * @returns {Promise<Object>} - Task status
   */
  checkTagExtractionStatus: async (taskId) => {
    try {
      return await tagTasksService.getTaskStatus(taskId);
    } catch (error) {
      throw handleApiError(error, 'checking tag extraction status');
    }
  },
  
  /**
   * Poll for tag extraction task status until completion or timeout
   * @param {string} taskId - ID of the task to poll
   * @param {number} intervalMs - Polling interval in ms (default 2000)
   * @param {number} timeoutMs - Maximum polling time in ms (default 60000)
   * @returns {Promise<Object>} - Final task result
   */
  pollTagExtractionStatus: async (taskId, intervalMs = 2000, timeoutMs = 60000) => {
    try {
      return await tagTasksService.pollTaskUntilComplete(taskId, intervalMs, timeoutMs);
    } catch (error) {
      throw handleApiError(error, 'polling tag extraction status');
    }
  },
  
  /**
   * Get the current tags for an item
   * @param {number} itemId - ID of the item
   * @returns {Promise<Object>} - Item tags
   */
  getItemTags: async (itemId) => {
    try {
      return await tagTasksService.getItemTags(itemId);
    } catch (error) {
      throw handleApiError(error, 'getting item tags');
    }
  },

  /**
   * Structure a raw voice-dictated transcript into a description and tags.
   * The backend already falls back internally on transient AI failures, so
   * this call is intentionally NOT wrapped in the module's retry helper.
   * @param {string} transcript - Raw transcript text from voice input
   * @param {string|null} itemName - Optional item name for extra context
   * @returns {Promise<{description: string, tags: string[]}>}
   */
  structureTranscript: async (transcript, itemName = null) => {
    const response = await api.post(
      '/ai/structure-transcript',
      { transcript, item_name: itemName },
      { timeout: 30000 }
    );
    return response.data;
  }
};

/**
 * Detect whether an error represents the backend's AI usage-limit rejection
 * (HTTP 403 with error_code 'ai_limit_reached'), as opposed to any other
 * 403/error shape.
 * @param {*} err - The error thrown by an axios call
 * @returns {boolean}
 */
export const isAiLimitError = (err) =>
  Boolean(
    err &&
      err.response &&
      err.response.status === 403 &&
      err.response.data &&
      err.response.data.error_code === 'ai_limit_reached'
  );

export default aiService;
